import React, { useCallback } from "react";
import { cn } from "@/utils/cn";
import { resolveTapAction, TapMode } from "./carouselTapNav";

interface CarouselTapZonesProps {
  mode: TapMode;
  onPrev: () => void;
  onNext: () => void;
  /** Center-band tap on a video slide; ignored in image mode. */
  onToggle?: () => void;
  className?: string;
}

/**
 * Transparent tap surface over the current carousel slide. Left/right edges
 * navigate; a video slide's wide center toggles play/pause. The zone geometry
 * lives in `resolveTapAction`, so it stays unit-tested and identical across
 * every call site.
 */
export const CarouselTapZones: React.FC<CarouselTapZonesProps> = ({
  mode,
  onPrev,
  onNext,
  onToggle,
  className,
}) => {
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const rect = e.currentTarget.getBoundingClientRect();
      const fraction =
        rect.width > 0 ? (e.clientX - rect.left) / rect.width : 0;
      const action = resolveTapAction(fraction, mode);
      if (action === "prev") onPrev();
      else if (action === "next") onNext();
      else onToggle?.();
    },
    [mode, onPrev, onNext, onToggle],
  );

  return (
    <div
      className={cn("absolute inset-0 z-20 cursor-pointer", className)}
      onClick={handleClick}
      aria-hidden="true"
    />
  );
};
